import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// === ОСНОВНОЙ КОНТЕЙНЕР ===
const Section = styled.section`
  padding: 8rem 2rem;
  background: linear-gradient(
    180deg,
    #000000ff 0%,
    #0d0d0d 35%,
    #0d0d0dff 50%,
    #0a0a0a 75%,
    #0a0a0a 100%
  );
  overflow: hidden;
  position: relative;
`;

const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 3rem;
  }
`;

// === ТЕКСТ ===
const TextContent = styled(motion.div)`
  color: white;
  padding-left: 1.5rem;
  max-width: 540px;

  @media (max-width: 768px) {
    padding-left: 0;
    margin: 0 auto;
  }
`;

const Title = styled.h2`
  font-size: 4.5rem;
  font-weight: 900;
  background: linear-gradient(135deg, #ffffff, #666666);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
  line-height: 1.08;
  letter-spacing: -1.5px;

  @media (max-width: 768px) {
    font-size: 2.9rem;
  }
`;

const Tagline = styled.p`
  font-size: 1.4rem;
  color: #e8e8e8;
  line-height: 1.7;
  margin-bottom: 2.2rem;
  font-weight: 400;
  opacity: 0.7;
`;

const Benefits = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Benefit = styled(motion.li)`
  font-size: 1.2rem;
  color: #f5f5f5;
  margin-bottom: 1.1rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  font-weight: 100;

  @media (max-width: 768px) {
    justify-content: left;
    text-align: left;
  }

  svg {
    width: 22px;
    height: 22px;
    stroke: #00f5ff;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
`;

// === ДЕВАЙСЫ ===
const DevicesWrapper = styled(motion.div)`
  display: flex;
  justify-content: flex-end;
  padding-right: 2rem;
  position: relative;
  perspective: 1800px;

  @media (max-width: 768px) {
    justify-content: center;
    padding-right: 0;
  }
`;

const PhoneLarge = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 340px;
  height: 680px;
  background: #000;
  border-radius: 48px;
  padding: 16px;
  box-shadow: 
    0 20px 50px rgba(0, 0, 0, 0.4),
    0 0 25px rgba(0, 245, 255, 0.1);

  @media (max-width: 768px) {
    width: 300px;
    height: 600px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    width: 70px;
    height: 8px;
    background: transparent;
    border-radius: 4px;
    z-index: 2;
  }
`;

const PhoneSmall = styled(motion.div)`
  position: absolute;
  bottom: -30px;
  left: 30px;
  z-index: 1;
  width: 260px;
  height: 520px;
  background: #000;
  border-radius: 42px;
  padding: 14px;
  transform: rotate(-12deg);
  opacity: 0.92;

  @media (max-width: 768px) {
    display: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 7px;
    background: transparent;
    border-radius: 4px;
    z-index: 2;
  }
`;

const PhoneScreen = styled.div`
  width: 100%;
  height: 100%;
  background-image: url('${props => props.image}');
  background-size: cover;
  background-position: center;
  border-radius: 32px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, transparent 40%, rgba(0, 245, 255, 0.06) 100%);
    border-radius: 32px;
    pointer-events: none;
  }
`;

// СКРИНШОТЫ (оставь свои пути)
const LARGE_SCREENSHOT = "/images/screen1.png";
const SMALL_SCREENSHOT = "/images/screen2.png";

const AboutApp = () => {
  const { t } = useTranslation();

  const benefits = [
    t('about_benefit_1'),
    t('about_benefit_2'),
    t('about_benefit_3'),
    t('about_benefit_4'),
    t('about_benefit_5')
  ];

  return (
    <Section>
      <Container>
        {/* ТЕКСТОВАЯ ЧАСТЬ */}
        <TextContent
          initial={{ opacity: 0, x: -80 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        >
          <Title>{t('about_title')}</Title>
          <Tagline>{t('about_tagline')}</Tagline>

          <Benefits>
            {benefits.map((text, i) => (
              <Benefit
                key={i}
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.6 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <path d="M5 13l4 4L19 7"/>
                </svg>
                {text}
              </Benefit>
            ))}
          </Benefits>
        </TextContent>

        {/* ДЕВАЙСЫ */}
        <DevicesWrapper
          initial={{ opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <PhoneSmall
            initial={{ opacity: 0, x: -100, y: 50, rotate: -20 }}
            whileInView={{ opacity: 0.92, x: 0, y: 0, rotate: -12 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, delay: 0.2 }}
          >
            <PhoneScreen image={SMALL_SCREENSHOT} />
          </PhoneSmall>

          <PhoneLarge
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <PhoneScreen image={LARGE_SCREENSHOT} />
          </PhoneLarge>
        </DevicesWrapper>
      </Container>
    </Section>
  );
};

export default AboutApp;